import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendInvoice } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Send } from 'lucide-react';

interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  currency: string;
  onSuccess: () => void;
}

export function SendInvoiceDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  totalAmount,
  currency,
  onSuccess,
}: SendInvoiceDialogProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [toAddress, setToAddress] = useState('');
  const [ccAddresses, setCcAddresses] = useState('');
  const [customMessage, setCustomMessage] = useState(
    `Please find attached invoice ${invoiceNumber} for ${formatCurrency(totalAmount, currency)}. Payment is due upon receipt.`,
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const ccList = ccAddresses
        .split(',')
        .map((addr) => addr.trim())
        .filter(Boolean);
      return sendInvoice(invoiceId, {
        toAddress: toAddress.trim(),
        ccAddresses: ccList.length > 0 ? ccList : undefined,
        customMessage: customMessage.trim() || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      addToast({ type: 'success', title: `Invoice ${invoiceNumber} sent successfully` });
      setToAddress('');
      setCcAddresses('');
      setError(null);
      onOpenChange(false);
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to send invoice');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send Invoice {invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            Send this invoice as a PDF attachment via email.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="to-address">To *</Label>
            <Input
              id="to-address"
              type="email"
              placeholder="recipient@example.com"
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cc-addresses">CC</Label>
            <Input
              id="cc-addresses"
              type="text"
              placeholder="cc1@example.com, cc2@example.com"
              value={ccAddresses}
              onChange={(e) => setCcAddresses(e.target.value)}
            />
            <p className="text-xs text-text-tertiary">Separate multiple addresses with commas</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="custom-message">Message</Label>
            <Textarea
              id="custom-message"
              placeholder="Enter a custom message..."
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={4}
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!toAddress.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Sending...' : 'Send Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
